import { create } from 'zustand';
import { subscribeWithSelector, persist } from 'zustand/middleware';
import { RoomTypeEnum } from '@danda/shared';
import type { LobbyState, Character, ChatMessage, ChatRoom } from './lobbyStore.types';
import { IS_DEV } from '@/lib/constants';
import { connectionSlice } from './lobbyStore.connection';
import { charactersSlice } from './lobbyStore.characters';
import { partiesSlice } from './lobbyStore.parties';
import { chatSlice } from './lobbyStore.chat';
import { notificationsSlice } from './lobbyStore.notifications';
import { uiSlice } from './lobbyStore.ui';
import { latencySlice } from './lobbyStore.latency';
import { charCreationSlice } from './lobbyStore.char-creation';
import { friendsSlice } from './lobbyStore.friends';
import { backgroundSlice } from './lobbyStore.background';
import { decisionsSlice } from './lobbyStore.decisions';
import { revealQueueSlice } from './lobbyStore.reveal-queue';
import { journalSlice } from './lobbyStore.journal';
import { handleLobbyEventFactory } from './lobbyStore.events';
import { clearSeenPartyImages } from './events/background-events';

type LobbySet = (
  partial: Partial<LobbyState> | ((state: LobbyState) => Partial<LobbyState>)
) => void;

const slices = [
  connectionSlice,
  charactersSlice,
  partiesSlice,
  chatSlice,
  notificationsSlice,
  uiSlice,
  latencySlice,
  charCreationSlice,
  friendsSlice,
  backgroundSlice,
  decisionsSlice,
  revealQueueSlice,
  journalSlice,
];

function cloneValue<T>(value: T): T {
  if (value instanceof Set) {
    return new Set(value) as unknown as T;
  }
  if (Array.isArray(value)) {
    return [...value] as unknown as T;
  }
  if (value && typeof value === 'object') {
    return { ...(value as Record<string, unknown>) } as unknown as T;
  }
  return value;
}

function extractState(slice: Record<string, unknown>): Partial<LobbyState> {
  const entries = Object.entries(slice).filter(([, val]) => typeof val !== 'function');
  return Object.fromEntries(entries.map(([key, val]) => [key, cloneValue(val)]));
}

function createInitialState(): LobbyState {
  return slices.reduce<Partial<LobbyState>>(
    (acc, slice) => ({ ...acc, ...extractState(slice) }),
    {}
  ) as LobbyState;
}

function bindActions(set: LobbySet): Partial<LobbyState> {
  const bound: Record<string, unknown> = {};
  for (const slice of slices) {
    for (const [key, value] of Object.entries(slice)) {
      if (typeof value === 'function') {
        bound[key] = ((...args: unknown[]) => {
          const result = (value as (...params: unknown[]) => unknown)(...args);
          if (typeof result === 'function') {
            set((state) => (result as (state: LobbyState) => Partial<LobbyState>)(state));
          } else {
            set(result as Partial<LobbyState>);
          }
        }) as LobbyState[keyof LobbyState];
      }
    }
  }
  return bound as Partial<LobbyState>;
}

export const useLobbyStore = create<LobbyState>()(
  subscribeWithSelector(
    persist(
      (set, get) => {
        const initialState = createInitialState();
        const actions = bindActions(set);

        return {
          ...initialState,
          ...actions,
          handleLobbyEvent: handleLobbyEventFactory(get, set),
          reset: () => {
            // Clear persisted storage to prevent stale data on user switch
            localStorage.removeItem('lobby-storage');
            clearSeenPartyImages();
            set(createInitialState());
          },
        } as LobbyState;
      },
      {
        name: 'lobby-storage',
        version: 6,
        partialize: (state) => ({
          selectedCharacterId: state.selectedCharacterId,
          activeChatRoomId: state.activeChatRoomId,
          chatRooms: state.chatRooms.map((room) => ({
            ...room,
            // Gate bubbles are ephemeral (in-chat low-balance hint that
            // ages out as soon as the balance changes). Persisting them
            // resurrects a stale "out of Polymyth Tokens" message after
            // the player tops up and reloads — filter them out before
            // serialization. Mirrors the gameStore.ts fix.
            messages: room.messages
              .map(({ isStreaming: _, isQueuedReveal: __, ...rest }) => rest)
              .filter((m) => (m.content || m.imageUrl) && m.type !== 'token-gate'),
          })),
        }),
        migrate: (persistedState: unknown, version: number) => {
          const state = persistedState as Partial<LobbyState>;

          if (version < 1) {
            if (state.chatRooms) {
              state.chatRooms = state.chatRooms.map((room) => {
                const roomType = (room as { type?: string }).type;
                if (
                  room.id === 'tavian' &&
                  (roomType === RoomTypeEnum.TAVIAN || roomType === RoomTypeEnum.ONE_TO_ONE)
                ) {
                  return { ...room, id: 'main', type: 'tavian' as const };
                }
                return room;
              });
            }
            if (state.activeChatRoomId === 'tavian') {
              state.activeChatRoomId = 'main';
            }
          }

          if (version < 2) {
            delete (state as Record<string, unknown>).mainChatNpc;
          }

          if (version < 3) {
            // Fix corrupted main room - ensure main room is always tavian type
            if (state.chatRooms) {
              state.chatRooms = state.chatRooms.map((room) => {
                if (room.id === 'main') {
                  return { ...room, type: 'tavian' as const, name: 'Tavian' };
                }
                return room;
              });
            }
          }

          if (version < 4) {
            // Sanitize persisted messages — drop entries missing required fields
            if (state.chatRooms) {
              state.chatRooms = state.chatRooms.map((room) => ({
                ...room,
                messages: (room.messages ?? []).filter(
                  (m) =>
                    m && typeof m.id === 'string' && typeof m.content === 'string' && m.timestamp
                ),
              }));
            }
          }

          if (version < 5) {
            // Strip ephemeral streaming state and remove resulting empty messages
            if (state.chatRooms) {
              state.chatRooms = state.chatRooms.map((room) => ({
                ...room,
                messages: (room.messages ?? [])
                  .map((m) => {
                    const { isStreaming: _, isQueuedReveal: __, ...rest } = m;
                    return rest;
                  })
                  .filter((m) => (m.content || m.imageUrl) && m.type !== 'token-gate'),
              }));
            }
          }

          if (version < 6) {
            // Strip already-leaked token-gate bubbles from pre-v6 payloads.
            // Without this, anyone who hit the gate before the deploy keeps
            // seeing the stale bubble on first reload post-deploy.
            if (state.chatRooms) {
              state.chatRooms = state.chatRooms.map((room) => ({
                ...room,
                messages: (room.messages ?? []).filter((m) => m.type !== 'token-gate'),
              }));
            }
          }

          return state as LobbyState;
        },
      }
    )
  )
);

if (IS_DEV) {
  (globalThis as { __lobbyState?: LobbyState }).__lobbyState = useLobbyStore.getState();
  useLobbyStore.subscribe((nextState) => {
    (globalThis as { __lobbyState?: LobbyState }).__lobbyState = nextState;
  });
}

export const useSelectedCharacter = () =>
  useLobbyStore((state) => {
    if (!state.selectedCharacterId) return null;
    return state.characters.find((c) => c.id === state.selectedCharacterId) ?? null;
  });

export const useCurrentParty = () =>
  useLobbyStore((state) => {
    if (!state.currentPartyId) return null;
    return state.parties.find((p) => p.id === state.currentPartyId) ?? null;
  });

export const useActiveChatRoom = () =>
  useLobbyStore((state) => state.chatRooms.find((r) => r.id === state.activeChatRoomId) ?? null);

export const useUnreadNotificationCount = () =>
  useLobbyStore((state) => state.notifications.filter((n) => !n.isRead).length);

export const usePendingInviteCount = () => useLobbyStore((state) => state.pendingInvites.length);

export type { Character, ChatMessage, ChatRoom };
